<template>
  <div class="page-container vip-user-page">
    <el-breadcrumb separator="/">
      <el-breadcrumb-item>
        <span class="page-title">VIP管理</span>
      </el-breadcrumb-item>
      <el-breadcrumb-item>VIP用户管理</el-breadcrumb-item>
    </el-breadcrumb>

    <el-card class="page-content" shadow="never">
      <el-form :inline="true" :model="query" size="small" class="query-form">
        <el-form-item label="用户UID">
          <el-input v-model.trim="query.userUid" clearable placeholder="请输入用户UID" />
        </el-form-item>
        <el-form-item label="顶级合伙人UID">
          <el-input v-model.trim="query.topPartnerUid" clearable placeholder="请输入顶级合伙人UID" />
        </el-form-item>
        <el-form-item label="VIP等级">
          <el-select v-model="query.vipLevel" clearable placeholder="全部">
            <el-option
              v-for="item in vipLevels"
              :key="item.code"
              :label="item.label"
              :value="Number(item.code)"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="变更时间">
          <el-date-picker
            v-model="query.timeRange"
            type="datetimerange"
            range-separator="至"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            value-format="timestamp"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="search">查询</el-button>
          <el-button @click="resetQuery">重置</el-button>
        </el-form-item>
      </el-form>

      <el-table
        :data="rows"
        border
        size="small"
        style="width: 100%"
        @sort-change="onSortChange"
      >
        <el-table-column prop="userUid" label="用户UID" width="120" align="center" />
        <el-table-column prop="userTypeName" label="用户类型" width="120" align="center" />
        <el-table-column prop="topPartnerUid" label="顶级合伙人UID" width="140" align="center">
          <template slot-scope="scope">{{ scope.row.topPartnerUid || '--' }}</template>
        </el-table-column>
        <el-table-column
          v-for="item in thresholdTypes"
          :key="item.code"
          :prop="item.code"
          :label="thresholdLabel(item)"
          min-width="210"
          sortable="custom"
          align="right"
        >
          <template slot-scope="scope">{{ amountText(thresholdValue(scope.row, item.code)) }}</template>
        </el-table-column>
        <el-table-column prop="vipLevel" label="当前VIP等级" width="130" sortable="custom" align="center">
          <template slot-scope="scope">VIP{{ scope.row.currentVipLevel || 0 }}</template>
        </el-table-column>
        <el-table-column label="晋级达标门槛" min-width="230" align="center">
          <template slot-scope="scope">{{ promotionThresholdText(scope.row) }}</template>
        </el-table-column>
        <el-table-column label="宽限天数" width="100" align="center">
          <template slot-scope="scope">{{ scope.row.retentionGraceDays === null || scope.row.retentionGraceDays === undefined ? '--' : scope.row.retentionGraceDays }}</template>
        </el-table-column>
        <el-table-column label="变更时间" width="170" align="center">
          <template slot-scope="scope">{{ formatTime(scope.row.changeTime) }}</template>
        </el-table-column>
        <el-table-column prop="vipExpireTime" label="到期时间" width="170" sortable="custom" align="center">
          <template slot-scope="scope">{{ formatTime(scope.row.vipExpireTime) }}</template>
        </el-table-column>
        <el-table-column label="变更方式" width="130" align="center">
          <template slot-scope="scope">{{ scope.row.changeTypeName || '--' }}</template>
        </el-table-column>
        <el-table-column label="操作" width="130" fixed="right" align="center">
          <template slot-scope="scope">
            <el-button
              v-if="canRecord"
              type="text"
              size="small"
              @click="openChangeRecord(scope.row)"
            >
              VIP变更记录
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pager"
        background
        layout="total, prev, pager, next, sizes"
        :total="total"
        :page-size="pageSize"
        :current-page.sync="page"
        :page-sizes="[10, 20, 50]"
        @size-change="onSizeChange"
        @current-change="fetchList"
      />
    </el-card>

    <el-dialog
      title="VIP变更记录"
      :visible.sync="recordVisible"
      width="640px"
    >
      <el-table :data="recordRows" border size="small" style="width: 100%">
        <el-table-column label="变更时间" width="170" align="center">
          <template slot-scope="scope">{{ formatTime(scope.row.changeTime) }}</template>
        </el-table-column>
        <el-table-column label="变更前VIP等级" width="130" align="center">
          <template slot-scope="scope">VIP{{ scope.row.beforeVipLevel || 0 }}</template>
        </el-table-column>
        <el-table-column label="变更后VIP等级" width="130" align="center">
          <template slot-scope="scope">VIP{{ scope.row.afterVipLevel || 0 }}</template>
        </el-table-column>
        <el-table-column prop="changeTypeName" label="变更方式" min-width="140" align="center" />
      </el-table>
      <el-pagination
        class="pager"
        background
        layout="total, prev, pager, next, sizes"
        :total="recordTotal"
        :page-size="recordPageSize"
        :current-page.sync="recordPage"
        :page-sizes="[10, 20, 50]"
        @size-change="onRecordSizeChange"
        @current-change="fetchChangeRecords"
      />
    </el-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { hasPerm } from 'common/permission';
import api from './api';

function emptyQuery() {
  return {
    userUid: '',
    topPartnerUid: '',
    vipLevel: '',
    timeRange: [],
  };
}

export default {
  name: 'VipUserPage',
  data() {
    return {
      query: emptyQuery(),
      rows: [],
      total: 0,
      page: 1,
      pageSize: 20,
      sortField: '',
      sortOrder: '',
      vipLevels: [],
      thresholdTypes: [],
      recordVisible: false,
      currentUser: null,
      recordRows: [],
      recordTotal: 0,
      recordPage: 1,
      recordPageSize: 20,
    };
  },
  computed: {
    ...mapState(['userInfo']),
    canRecord() {
      return hasPerm(this.userInfo || {}, 'tenant:vip_user:change_record');
    },
  },
  created() {
    this.loadEnums();
    this.fetchList();
  },
  methods: {
    async loadEnums() {
      const res = await api.enums();
      const data = (res && res.data) || {};
      this.vipLevels = data.vipLevels || [];
      this.thresholdTypes = data.thresholdTypes || [];
    },
    async fetchList() {
      const params = {
        page: this.page,
        size: this.pageSize,
        userUid: this.query.userUid,
        topPartnerUid: this.query.topPartnerUid,
        vipLevel: this.query.vipLevel,
        sortField: this.sortField,
        sortOrder: this.sortOrder,
      };
      if (this.query.timeRange && this.query.timeRange.length === 2) {
        params.startTime = this.query.timeRange[0];
        params.endTime = this.query.timeRange[1];
      }
      const res = await api.page(params);
      const data = (res && res.data) || {};
      this.rows = data.items || [];
      this.total = data.total || 0;
    },
    search() {
      this.page = 1;
      this.fetchList();
    },
    resetQuery() {
      this.query = emptyQuery();
      this.sortField = '';
      this.sortOrder = '';
      this.page = 1;
      this.fetchList();
    },
    onSizeChange(size) {
      this.pageSize = size;
      this.page = 1;
      this.fetchList();
    },
    onSortChange({ prop, order }) {
      this.sortField = prop || '';
      this.sortOrder = order || '';
      this.page = 1;
      this.fetchList();
    },
    openChangeRecord(row) {
      this.currentUser = row;
      this.recordPage = 1;
      this.recordVisible = true;
      this.fetchChangeRecords();
    },
    async fetchChangeRecords() {
      if (!this.currentUser) return;
      const res = await api.changeRecordPage({
        page: this.recordPage,
        size: this.recordPageSize,
        userId: this.currentUser.userId,
      });
      const data = (res && res.data) || {};
      this.recordRows = data.items || [];
      this.recordTotal = data.total || 0;
    },
    onRecordSizeChange(size) {
      this.recordPageSize = size;
      this.recordPage = 1;
      this.fetchChangeRecords();
    },
    thresholdLabel(item) {
      return item.unit ? `${item.label}(${item.unit})` : item.label;
    },
    thresholdValue(row, code) {
      return row.thresholdValues && row.thresholdValues[code] !== undefined
        ? row.thresholdValues[code]
        : 0;
    },
    promotionThresholdText(row) {
      if (!row || !row.promotionThresholdName) return '--';
      const value = row.promotionThresholdActualValue;
      if (value === null || value === undefined || value === '') return '--';
      const unit = row.promotionThresholdUnit ? ` ${row.promotionThresholdUnit}` : '';
      return `${row.promotionThresholdName}=${this.amountText(value)}${unit}`;
    },
    amountText(value) {
      if (value === null || value === undefined || value === '') return '0';
      return Number(value).toLocaleString(undefined, { maximumFractionDigits: 8 });
    },
    formatTime(value) {
      if (!value) return '--';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      const pad = (num) => String(num).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    },
  },
};
</script>

<style scoped>
.vip-user-page .page-content {
  margin-top: 16px;
}
.query-form {
  margin-bottom: 12px;
}
/deep/ .el-table th.is-sortable .cell {
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
}
/deep/ .el-table th.is-sortable .caret-wrapper {
  flex: 0 0 auto;
}
.pager {
  margin-top: 16px;
  text-align: right;
}
</style>
